import { nameValidation } from "components/billing/nameValidation";
import { Button } from "components/DesignSystem/Button/Button";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { SolidCross } from "components/icons/SolidCross";
import { OtpResendTimer } from "components/magicLink/OtpResendTimerText";
import {
  MOBILE_NUMBER_SUBMITTED,
  MOBILE_NUMBER_SUBMIT_FAILURE,
  MOBILE_VERIFICATION_FAILURE,
  MOBILE_VERIFICATION_SUCCESS,
} from "constants/analyticsEvents";
import { SOMETHING_WENT_WRONG } from "constants/apiCallError";
import { AFTER_AUTH_REDIRECT_TO, MCP_NEXT_URL } from "constants/storageKeys";
import { FOREIGN_CA } from "constants/userTypes";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { FormikValues } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  useSendOtpForMobileMutation,
  useUpdateNameMutation,
  useVerifyMobileOtpMutation,
} from "store/apis/settings";
import { setOnboardingStep } from "store/slices/onboarding";
import { Profile } from "types/Models/user";
import { BackendError } from "types/utils/error";
import { getCurrentTimezone } from "utils/getCurrentTimezone";
import { getGoogleUser } from "apis/googleAuthApis";
import { signupResendOtp } from "apis/signupResendOtp";
import { INCORRECT_OTP_CODE, OTP_SENT_SUCCESSFULLY } from "constants/magicLink";
import authContext from "jwt_context&axios/authContext";
import { debounce } from "utils/debouncing";
import OtpBox from "./otpBox";
import { StepIndicator } from "components/Auth/StepIndicator";
import { useUserOnboardingStepper } from "hooks/useUserOnboardingStepper";
import { ChatCartPaid } from "components/icons/Chat/ChatCartPaid";
import { CouponCodeVerify, getStoredCouponCode } from "./CouponCodeVerify";
import { isCustomerSignupUser } from "utils/isCustomerSignupUser";
import { isOnServiceTeam } from "utils/isOnServiceTeam";
import { getGoogleTosConsent } from "utils/googleTosConsent/getGoogleTosConsent";

dayjs.extend(timezone);
dayjs.extend(utc);

export const GoogleAuth = ({
  googleAccessToken,
  setLoading,
  onGoogleAuthFail,
  invitation = false,
  askPhoneNumber,
  setAskPhoneNumber,
  viewOnly,
}: {
  googleAccessToken?: string;
  setLoading?: (loading: boolean) => void;
  onGoogleAuthFail?: () => void;
  invitation?: boolean;
  askPhoneNumber: boolean;
  setAskPhoneNumber: (val: boolean) => void;
  viewOnly?: boolean;
}) => {
  let query = useQuery();
  const { alertToast, successToast } = useToast();
  const refCode = query.get("ref");
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const [mobile, setMobile] = useState("");
  const [profileId, setProfileId] = useState("");
  const [userDetails, setUserDetails] = useState<Profile>();
  const [inCorrectOtp, setInCorrectOtp] = useState(false);
  const [invalidOtp, setInvalidOtp] = useState(false);
  const [isChecked, setChecked] = useState<boolean>(true);
  const [showTimer, setShowTimer] = useState(false);
  const [key, setKey] = useState(0);
  const { setUser, setAuthtoken, authtoken } = useContext(authContext);
  const [otp, setOtp] = useState("");
  const [sendOtpForMobile] = useSendOtpForMobileMutation();
  const [verifyMobileOtp] = useVerifyMobileOtpMutation();

  let userData = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const { localData: refdata, removeItem: removeReferralCode } =
    useLocalStorage("referralCode", null);
  const referralCode = refCode || refdata;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isNameUnavailable = userData.name === "";
  const [updateName] = useUpdateNameMutation();
  const storedCoupon = localStorage.getItem("couponCode");
  const { currentStepNumber, initialize } = useUserOnboardingStepper();
  const [showApplyCouponCode, setShowApplyCouponCode] = useState(
    Boolean(storedCoupon)
  );

  const redirectToProfile = () => {
    initialize("BRAND_CREATE");
    history.push("/profile");
  };

  const redirectToSignup = () => history.push("/signup");

  const setUserData = (user: Profile) => {
    setAuthtoken(user);
    setUser(user.access);
    localStorage.setItem("authTokens", JSON.stringify(user));

    const profile = user;
    const isPartOfServiceTeam = profile.is_any_service_user;
    const isForeignCA = profile.user_type === FOREIGN_CA;
    const isForeignCATeamMember = profile.is_foreign_ca;

    if (isForeignCATeamMember) {
      history.push("/practice");
      return;
    }

    if (isForeignCA && !isPartOfServiceTeam) {
      redirectToProfile();
      return;
    }

    if (user.company_group && user.is_public_user && !user.is_restricted) {
      history.push(`/filings/${user.company_group}`);
    } else if (
      (user.company_group && user.is_public_user && user.is_restricted) ||
      user.is_restricted
    ) {
      history.push("/verify");
    } else if (user.is_archived && user.is_group_created) {
      history.push("/archived");
    } else if (user.is_group_created) {
      const mcpNextUrl = localStorage.getItem(MCP_NEXT_URL);
      if (mcpNextUrl) {
        localStorage.removeItem(MCP_NEXT_URL);
        history.replace(`/mcp-confirm?next=${encodeURIComponent(mcpNextUrl)}`);
      } else {
        // move to dashboard
        history.replace(localStorage.getItem(AFTER_AUTH_REDIRECT_TO) || "/");
      }
    } else if (isOnServiceTeam(profile)) {
      history.push("/");
    } else if (isCustomerSignupUser(profile)) {
      redirectToSignup();
    } else {
      redirectToProfile();
    }
  };

  const getGoogleUserData = async () => {
    const session_id = localStorage.getItem("TASK_SESSION_ID");
    try {
      const response = await getGoogleUser({
        access_token: googleAccessToken,
        session_id,
        accept_tos: getGoogleTosConsent(),
      });
      setProfileId(response.data.data?.uuid);
      setUserDetails(response.data.data);

      const isMobileMissing = !response.data.data?.mobile;

      const isV2Signup =
        !invitation && isCustomerSignupUser(response.data.data);

      if (isMobileMissing && isV2Signup) {
        localStorage.setItem("currentUser", JSON.stringify(response.data.data));
        redirectToSignup();
      } else if (isMobileMissing) {
        setAskPhoneNumber(true);
      } else {
        setUserData(response.data.data);
      }
      setLoading?.(false);
    } catch (error: any) {
      onGoogleAuthFail?.();
      setLoading?.(false);
      if (googleAccessToken === "access_denied") {
        alertToast({
          message: "Access denied, please try again!",
          position: "bottom-left",
        });
        return;
      }
      if (typeof error?.response?.data?.error?.message === "string") {
        alertToast({
          message: error?.response?.data?.error?.message,
          position: "bottom-left",
        });
        return;
      }
      alertToast({ message: SOMETHING_WENT_WRONG, position: "bottom-left" });
    }
  };

  useEffect(() => {
    dispatch(setOnboardingStep(2));
    if (googleAccessToken) {
      getGoogleUserData();
    } else {
      setProfileId(userData?.uuid);
    }
  }, []);

  useEffect(() => {
    if (otp.length < 6) {
      setInCorrectOtp(false);
    }
  }, [otp]);

  const handleReset = () => {
    setOtp("");
    setInCorrectOtp(false);
    setInvalidOtp(false);
  };

  const isTosAccepted = googleAccessToken ? getGoogleTosConsent() : true;

  const addMobileNumber = async (values: FormikValues) => {
    setIsLoading(true);
    const phone = values.phone.replace(/ /g, "");
    setMobile(phone);
    const magicLinkData = localStorage.getItem("magicLinkData");
    const partialUser = magicLinkData ? JSON.parse(magicLinkData) : {};
    try {
      if (isNameUnavailable) {
        const user = await updateName({
          payload: {
            first_name: values?.first_name,
            last_name: values?.last_name,
            profile_id: profileId,
          },
        }).unwrap();
        localStorage.setItem("currentUser", JSON.stringify(user));
      }
      await sendOtpForMobile({
        mobile: phone,
        email: partialUser?.email || userDetails?.email || userData?.email,
        whatsapp_consent: isChecked,
        accept_tos: isTosAccepted,
      }).unwrap();

      trackEvent(MOBILE_NUMBER_SUBMITTED, {
        screen: "Add Mobile number",
        is_whatsapp_checked: isChecked,
        email: partialUser?.email || userDetails?.email || userData?.email,
        mobile: phone,
      });

      setAskPhoneNumber(false);
    } catch (error: any) {
      trackEvent(MOBILE_NUMBER_SUBMIT_FAILURE, {
        screen: "Add Mobile number",
        email: partialUser?.email || userDetails?.email || userData?.email,
        mobile: phone,
        error_message: error?.response?.data?.error?.message,
      });
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
        position: "bottom-left",
      });
    }
    setIsLoading(false);
  };

  const verifyOtp = async () => {
    setIsLoading(true);
    const magicLinkData = localStorage.getItem("magicLinkData");

    const partialUser = magicLinkData ? JSON.parse(magicLinkData) : {};
    try {
      const couponCode = getStoredCouponCode();
      //@ts-ignore
      const user = await verifyMobileOtp({
        payload: {
          mobile: mobile.toString(),
          profile_id: profileId || partialUser.uuid,
          otp,
          coupon_code: couponCode || undefined,
          wa_consent: isChecked,
          referral_code: referralCode!,
          current_timezone: getCurrentTimezone(),
        },
      }).unwrap();

      if (user) {
        setUserData(user);
        removeReferralCode();
        localStorage.removeItem("couponCode");
        trackEvent(MOBILE_VERIFICATION_SUCCESS, {
          screen: "OTP Verification - Mobile",
          is_whatsapp_checked: isChecked,
          email: partialUser?.email || userDetails?.email || userData?.email,
          mobile: mobile,
        });
      }
    } catch (error: any) {
      trackEvent(MOBILE_VERIFICATION_FAILURE, {
        screen: "OTP Verification - Mobile",
        email: partialUser?.email || userDetails?.email || userData?.email,
        mobile: mobile,
        error_message: error?.response?.data?.error?.message,
      });
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
        position: "bottom-left",
      });
      updateData(error?.response?.data?.error?.code);
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = debounce(() => verifyOtp());

  const updateData = (code: string) => {
    if (code === INCORRECT_OTP_CODE) setInCorrectOtp(true);
  };

  const resendOtp = async () => {
    const magicLinkData = localStorage.getItem("magicLinkData");
    const partialUser = magicLinkData ? JSON.parse(magicLinkData) : {};
    try {
      const values = {
        phone_no: mobile.toString(),
        email: partialUser?.email || userDetails?.email || userData?.email,
      };
      let res = await signupResendOtp({ values, isMailOtp: false });
      setOtp("");
      setShowTimer(true);
      setKey((prev) => prev + 1);
      successToast({ message: OTP_SENT_SUCCESSFULLY, position: "bottom-left" });
    } catch (e: any) {
      alertToast({
        message: e?.response?.data?.error?.message,
        position: "bottom-left",
      });
    }
  };

  return (
    <div className="t-w-full">
      {!invitation && currentStepNumber && (
        <StepIndicator
          currentStep={currentStepNumber}
          totalSteps={4}
          label="Mobile verification"
        />
      )}
      <div className="t-text-subtitle-lg t-text-text-100 t-mb-2">
        {isNameUnavailable
          ? "Add your details & verify mobile number"
          : "Verify your mobile number"}
      </div>
      <div className="t-text-body-sm t-text-text-30 t-mb-8">
        {askPhoneNumber
          ? `Enter your mobile number to receive a
        verification OTP.`
          : `Please enter the OTP sent to ${mobile}.`}
      </div>
      {askPhoneNumber ? (
        <FormikForm
          initialValues={{
            phone: mobile || authtoken?.mobile || "",
            first_name: "",
            last_name: "",
          }}
          onSubmit={addMobileNumber}
          validateOnChange
          validationSchema={() => nameValidation(isNameUnavailable)}
        >
          {({ isValid }) => {
            return (
              <>
                {isNameUnavailable && (
                  <div className="t-flex t-w-full t-gap-4 t-mb-2">
                    <TextInput
                      placeholder="John"
                      label="First name"
                      name="first_name"
                      required
                    />
                    <TextInput
                      placeholder="Smith"
                      label="Last name"
                      name="last_name"
                      required
                    />
                  </div>
                )}
                <div>
                  <MobileInput
                    label="Enter mobile number"
                    name="phone"
                    placeholder="Phone Number"
                    required
                    disabled={viewOnly}
                    rightComponent={viewOnly && <ChatCartPaid />}
                    validateOnChange={false}
                    validateOnBlur={false}
                  />
                </div>
                <div className="t-flex t-items-center t-justify-between t-mt-4 t-mb-8 t-gap-4">
                  <div className="t-shrink-0 t-flex t-justify-center t-items-center">
                    <Switch
                      checked={isChecked}
                      name="whatsappConsent"
                      onCheckedChange={setChecked}
                      disabled={viewOnly}
                    />
                  </div>
                  <label
                    htmlFor="whatsappConsent"
                    className="t-cursor-pointer t-w-full t-flex t-flex-col t-gap-0.5"
                  >
                    <div className="t-text-subtext t-text-text-60">
                      Enable WhatsApp notifications
                    </div>
                    <div className="t-text-body-sm t-text-text-30">
                      Allow important compliance updates & requests via
                      WhatsApp.
                    </div>
                  </label>
                </div>
                {!viewOnly && (
                  <Button
                    customType="primary"
                    size="large"
                    block
                    disabled={isLoading}
                    isLoading={isLoading}
                    type="submit"
                    rightIcon={<LongArrowRight />}
                  >
                    Continue
                  </Button>
                )}
              </>
            );
          }}
        </FormikForm>
      ) : (
        <>
          <div className="t-flex t-flex-col t-gap-6">
            <div>
              <OtpBox
                otp={otp}
                setOtp={setOtp}
                inCorrectOtp={inCorrectOtp}
                getOtp={setOtp}
                width="100%"
                needOtpDetail
                setInvalidOtp={setInvalidOtp}
                className="t-mb-3 t-justify-start"
                handleEnter={verifyOtp}
                reset={handleReset}
              />{" "}
              {inCorrectOtp && (
                <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-red t-font-light t-mb-3">
                  <SolidCross />
                  <span>Invalid OTP. Please try again!</span>
                </div>
              )}
              <div>
                {showTimer ? (
                  <OtpResendTimer setShowTimer={setShowTimer} />
                ) : (
                  <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1 t-italic">
                    Did not receive it?
                    <Button customType="link" size="xsmall" onClick={resendOtp}>
                      Resend OTP
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {showApplyCouponCode ? (
              <CouponCodeVerify />
            ) : (
              <Button
                customType="link"
                size="small"
                onClick={() => setShowApplyCouponCode(true)}
              >
                Apply a coupon code
              </Button>
            )}

            <Button
              customType="primary"
              size="large"
              block
              onClick={handleVerifyOtp}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Verify OTP
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
